import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function LegalLayout({ title, children }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800 transition-colors" data-testid="legal-back-btn">
            <ArrowLeft size={20} />
          </button>
          <Link to="/login" className="flex items-center gap-2">
            <img src="/chantix-logo.png" alt="CHANTIX" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8" data-testid="legal-page-title">{title}</h1>
        <div className="prose prose-sm sm:prose-base prose-gray max-w-none space-y-6">
          {children}
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white mt-12">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
            <Link to="/cgv" className="hover:text-gray-800 transition-colors">CGV</Link>
            <span className="text-gray-300">|</span>
            <Link to="/mentions-légales" className="hover:text-gray-800 transition-colors">Mentions légales</Link>
            <span className="text-gray-300">|</span>
            <Link to="/confidentialité" className="hover:text-gray-800 transition-colors">Confidentialité</Link>
            <span className="text-gray-300">|</span>
            <Link to="/cgu" className="hover:text-gray-800 transition-colors">CGU</Link>
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">&copy; {new Date().getFullYear()} GABRYX — CHANTIX. Tous droits réserves.</p>
        </div>
      </footer>
    </div>
  );
}

export function CGVPage() {
  return (
    <LegalLayout title="Conditions Générales de Vente (CGV)">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mt-0">Article 1 — Objet</h2>
        <p className="text-gray-600 leading-relaxed">
          Les présentes Conditions Générales de Vente régissent l'accès et l'utilisation de la plateforme <strong>CHANTIX</strong>, éditée par <strong>GABRYX</strong>, spécialisée dans les solutions SaaS de gestion OPC et CSPS pour le secteur du bâtiment.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 2 — Éditeur</h2>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
          <p><strong>GABRYX</strong></p>
          <p>Micro-entreprise / Entrepreneur individuel</p>
          <p>SIREN / RCS : 499 913 622 R.C.S. Poitiers</p>
          <p>Adresse : 35 rue de la Paix, 86300 Chauvigny, France</p>
          <p>Email : contact@gabryx.fr</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 3 — Offres et abonnements</h2>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg p-3">
            <span className="font-medium text-gray-800">CHANTIX OPC + AXEL</span>
            <span className="font-bold text-[#0055FF]">59 EUR / mois</span>
          </div>
          <div className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-lg p-3">
            <span className="font-medium text-gray-800">CHANTIX CSPS + Gaby</span>
            <span className="font-bold text-[#FF6B00]">59 EUR / mois</span>
          </div>
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            <span className="font-medium text-gray-800">CHANTIX Pack Pro (OPC + CSPS + AXEL + Gaby)</span>
            <span className="font-bold text-emerald-600">79 EUR / mois</span>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 4 — Paiement</h2>
        <p className="text-gray-600 leading-relaxed">
          Les abonnements sont facturés mensuellement via <strong>Stripe</strong>. Le paiement est reconduit automatiquement sauf résiliation par le client. Les données bancaires sont traitées directement par Stripe et ne sont jamais stockées par GABRYX.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 5 — Résiliation</h2>
        <p className="text-gray-600 leading-relaxed">
          Le client peut résilier son abonnement à tout moment depuis son espace utilisateur. L'accès au service reste actif jusqu'à la fin de la période payée. Aucun remboursement partiel n'est effectué pour la période en cours.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 6 — Responsabilité</h2>
        <p className="text-gray-600 leading-relaxed">
          GABRYX met en oeuvre tous les moyens raisonnables pour assurer la disponibilité et le bon fonctionnement du service, sans garantie d'absence totale d'erreurs ou d'interruptions. GABRYX ne saurait etre tenu responsable des dommages indirects lies a l'utilisation du service.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 7 — Propriété des données</h2>
        <p className="text-gray-600 leading-relaxed">
          Le client reste à tout moment propriétaire de ses données chantier. En cas de résiliation, les données peuvent être exportées sur demande dans un délai raisonnable.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 8 — Droit applicable</h2>
        <p className="text-gray-600 leading-relaxed">
          Les présentes conditions sont soumises au droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute action judiciaire. À défaut, les tribunaux compétents de Poitiers seront seuls compétents.
        </p>
      </section>

      <div className="pt-4 border-t border-gray-200 text-xs text-gray-400">
        Dernière mise a jour : mai 2026
      </div>
    </LegalLayout>
  );
}

export function MentionsLegalesPage() {
  return (
    <LegalLayout title="Mentions légales">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mt-0">Éditeur du site</h2>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
          <p><strong>Nom commercial :</strong> GABRYX</p>
          <p><strong>Produit :</strong> CHANTIX</p>
          <p><strong>Forme juridique :</strong> Micro-entreprise / Entrepreneur individuel</p>
          <p><strong>SIREN / RCS :</strong> 499 913 622 R.C.S. Poitiers</p>
          <p><strong>Adresse :</strong> 35 rue de la Paix, 86300 Chauvigny, France</p>
          <p><strong>Responsable de publication :</strong> Gabriel Castellanos-Riviere</p>
          <p><strong>Email :</strong> contact@gabryx.fr</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Hébergement</h2>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
          <p><strong>Hébergeur :</strong> Emergent / infrastructure associee</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Propriété intellectuelle</h2>
        <p className="text-gray-600 leading-relaxed">
          L'ensemble du contenu de la plateforme CHANTIX (textes, images, logos, fonctionnalites, code source) est protégé par le droit d'auteur et reste la propriété exclusive de GABRYX. Toute reproduction, représentation ou exploitation non autorisée est interdite.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Contact</h2>
        <p className="text-gray-600 leading-relaxed">
          Pour toute question relative aux mentions légales, vous pouvez nous contacter a l'adresse : <a href="mailto:contact@gabryx.fr" className="text-[#0055FF] hover:underline font-medium">contact@gabryx.fr</a>
        </p>
      </section>

      <div className="pt-4 border-t border-gray-200 text-xs text-gray-400">
        Dernière mise a jour : mai 2026
      </div>
    </LegalLayout>
  );
}

export function ConfidentialitePage() {
  return (
    <LegalLayout title="Politique de confidentialité">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mt-0">Introduction</h2>
        <p className="text-gray-600 leading-relaxed">
          GABRYX s'engage à protéger la vie privée de ses utilisateurs. La présente politique décrit les données collectées, leur utilisation et les droits dont vous disposez, conformément au Règlement Général sur la Protection des Données (RGPD).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">1. Données collectées</h2>
        <ul className="list-disc pl-5 text-gray-600 space-y-1">
          <li>Nom et prenom</li>
          <li>Adresse email</li>
          <li>Informations relatives aux chantiers (adresses, descriptions, documents)</li>
          <li>Documents professionnels générés ou importes</li>
          <li>Données de paiement (traitées par Stripe)</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">2. Utilisation des données</h2>
        <p className="text-gray-600 leading-relaxed mb-2">Les données collectées sont utilisées exclusivement pour :</p>
        <ul className="list-disc pl-5 text-gray-600 space-y-1">
          <li>La gestion des comptes utilisateurs</li>
          <li>La generation automatisée de documents (rapports, PGC, PPSPS...)</li>
          <li>Le bon fonctionnement du service SaaS</li>
          <li>Les assistants IA intégrés (<strong>AXEL</strong> — Assistant eXpert En Ligne — et <strong>Gaby</strong> — expert vision CSPS) : vos données de chantier, réserves, tâches, visites et comptes rendus sont transmises de façon chiffrée aux modèles Claude Sonnet 4.6 (Anthropic) et Gemini (Google) via nos intégrations sécurisées pour générer des réponses contextualisées. Aucune conversation n'est utilisée pour ré-entraîner les modèles.</li>
          <li>Le support client</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-2">
          <strong>Aucune donnee n'est vendue ou partagee a des tiers</strong> a des fins commerciales.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">3. Conservation des données</h2>
        <p className="text-gray-600 leading-relaxed">
          Les données sont conservées pendant la durée du contrat d'abonnement et selon les obligations légales françaises en vigueur. A la suppression du compte, les données sont effacées dans un délai de 30 jours.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">4. Vos droits (RGPD)</h2>
        <p className="text-gray-600 leading-relaxed mb-2">Conformément au RGPD, vous disposez des droits suivants :</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {['Droit d\'acces', 'Droit de rectification', 'Droit de suppression', 'Droit à la portabilité', 'Droit d\'opposition', 'Droit à la limitation du traitement'].map(droit => (
            <div key={droit} className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-sm text-gray-700">
              <svg className="w-4 h-4 text-[#0055FF] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              {droit}
            </div>
          ))}
        </div>
        <p className="text-gray-600 leading-relaxed mt-3">
          Pour exercer vos droits, contactez-nous a : <a href="mailto:contact@chantix.pro" className="text-[#0055FF] hover:underline font-medium">contact@chantix.pro</a>
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">5. Sécurité des paiements</h2>
        <p className="text-gray-600 leading-relaxed">
          Les paiements sont sécurisés via <strong>Stripe</strong> (certifié PCI DSS Niveau 1). GABRYX ne stocke pas directement les données bancaires. Seul Stripe a acces aux informations de carte de crédit.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">6. Cookies</h2>
        <p className="text-gray-600 leading-relaxed">
          CHANTIX utilise uniquement des cookies strictement nécessaires au fonctionnement du service (authentification, session). Aucun cookie publicitaire ou de tracage n'est utilise.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">7. Accès support technique et traçabilité</h2>
        <p className="text-gray-600 leading-relaxed">
          Dans le cadre du support technique, un administrateur GABRYX peut être amené à se connecter temporairement à votre espace CHANTIX pour reproduire un incident ou vous accompagner (notamment lors de la phase pilote ou d'un onboarding). Cet accès est strictement encadré :
        </p>
        <ul className="list-disc list-inside text-gray-600 mt-2 ml-2 space-y-1 text-sm">
          <li>chaque connexion administrateur est tracée dans un journal d'audit (identité de l'administrateur, date et heure de début/fin, adresse IP) ;</li>
          <li>une bannière visible en permanence signale à l'administrateur qu'il agit en mode support sur votre compte ;</li>
          <li>aucune donnée n'est modifiée sans votre accord explicite préalable ;</li>
          <li>vous pouvez à tout moment demander la consultation complète du journal d'audit concernant votre compte.</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-3">
          Pour exercer ce droit ou pour vous opposer à ce type d'accès, contactez-nous à : <a href="mailto:contact@chantix.pro" className="text-[#0055FF] hover:underline font-medium">contact@chantix.pro</a>
        </p>
      </section>

      <div className="pt-4 border-t border-gray-200 text-xs text-gray-400">
        Dernière mise a jour : juillet 2026 — Ajout des assistants IA AXEL &amp; Gaby.
      </div>
    </LegalLayout>
  );
}

export function CGUPage() {
  return (
    <LegalLayout title="Conditions Générales d'Utilisation (CGU)">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mt-0">Article 1 — Usage autorisé</h2>
        <p className="text-gray-600 leading-relaxed">
          CHANTIX est destiné exclusivement a un <strong>usage professionnel</strong> lie a la gestion de chantier dans le secteur du bâtiment et des travaux publics (BTP). L'utilisateur s'engage a utiliser la plateforme conformément a sa destination.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 2 — Compte utilisateur</h2>
        <p className="text-gray-600 leading-relaxed">
          L'utilisateur est responsable de la confidentialité de ses identifiants de connexion. Toute activité réalisée sous son compte est présumée effectuée par lui. En cas de suspicion d'acces non autorisé, l'utilisateur doit en informer GABRYX immédiatement.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 3 — Restrictions</h2>
        <p className="text-gray-600 leading-relaxed mb-2">Il est strictement interdit de :</p>
        <ul className="space-y-2">
          {[
            'Revendre, sous-licencier ou redistribuer l\'acces au service sans autorisation écrite de GABRYX',
            'Detourner le service de son usage prevu ou l\'utiliser a des fins illicites',
            'Tenter de compromettre la sécurité de la plateforme (intrusion, injection, etc.)',
            'Extraire systématiquement des données de la plateforme (scraping)',
            'Usurper l\'identite d\'un autre utilisateur',
          ].map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
              {r}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 4 — Suspension et résiliation</h2>
        <p className="text-gray-600 leading-relaxed">
          GABRYX se réserve le droit de suspendre ou résilier un compte utilisateur en cas de :
        </p>
        <ul className="list-disc pl-5 text-gray-600 space-y-1 mt-2">
          <li>Fraude ou tentative de fraude</li>
          <li>Impaye ou defaut de paiement</li>
          <li>Usage abusif ou non conforme aux présentes CGU</li>
          <li>Atteinte a la sécurité ou au fonctionnement de la plateforme</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 5 — Disponibilite du service</h2>
        <p className="text-gray-600 leading-relaxed">
          GABRYX s'efforce de maintenir le service accessible 24h/24 et 7j/7, mais ne garantit pas une disponibilité ininterrompue. Des maintenances programmées ou des incidents techniques peuvent entraîner des interruptions temporaires.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 6 — Assistants IA intégrés (AXEL &amp; Gaby)</h2>
        <p className="text-gray-600 leading-relaxed">
          CHANTIX intègre deux assistants IA optionnels, activables par un administrateur :
        </p>
        <ul className="list-disc pl-5 text-gray-600 space-y-1 mt-2">
          <li><strong>AXEL</strong> (Assistant eXpert En Ligne) : assistant conversationnel dédié au suivi de vos chantiers, de vos missions et de votre activité. Il exploite uniquement vos propres données CHANTIX (chantiers, réserves, planning, comptes rendus).</li>
          <li><strong>Gaby</strong> : expert vision CSPS analysant les photos de chantier pour formuler des observations sécurité.</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-2">
          Ces assistants sont fournis à titre d'<strong>outil d'aide à la décision</strong>. Ils ne remplacent en aucun cas le jugement du professionnel OPC ou CSPS ni sa responsabilité légale. L'utilisateur doit systématiquement vérifier les informations fournies avant toute action opérationnelle. Les conversations sont conservées pour vous permettre de les consulter et peuvent être supprimées à tout moment depuis l'interface.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 7 — Accès support et traçabilité</h2>
        <p className="text-gray-600 leading-relaxed">
          Dans le cadre d'une demande de support technique (incident, accompagnement, formation), un administrateur GABRYX peut être amené à accéder temporairement à votre compte CHANTIX afin de reproduire un problème ou vous assister. Cet accès est strictement limité aux besoins du support et fait l'objet d'une traçabilité complète et auditable : chaque connexion est enregistrée (identité de l'administrateur, date/heure de début et de fin, adresse IP) dans un journal d'audit interne. Vous pouvez à tout moment demander la consultation de ce journal en écrivant à <a href="mailto:contact@chantix.pro" className="text-[#0055FF] hover:underline font-medium">contact@chantix.pro</a>. Aucune modification de vos données ne sera effectuée sans votre accord explicite préalable.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 8 — Modifications</h2>
        <p className="text-gray-600 leading-relaxed">
          GABRYX se réserve le droit de modifier les présentes CGU. Les utilisateurs seront informés de toute modification substantielle. La poursuite de l'utilisation du service après notification vaut acceptation des nouvelles conditions.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Article 9 — Contact</h2>
        <p className="text-gray-600 leading-relaxed">
          Pour toute question relative a ces conditions, contactez-nous a : <a href="mailto:contact@chantix.pro" className="text-[#0055FF] hover:underline font-medium">contact@chantix.pro</a>
        </p>
      </section>

      <div className="pt-4 border-t border-gray-200 text-xs text-gray-400">
        Dernière mise a jour : juillet 2026 — Ajout de l'Article 6 sur les assistants IA (AXEL &amp; Gaby).
      </div>
    </LegalLayout>
  );
}
